import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";

// Google Analytics is already loaded in index.html, so we don't need to load it here again
// This prevents duplicate loading and potential conflicts

// Helper function to check if error should be suppressed
const shouldSuppressError = (event: ErrorEvent): boolean => {
  // Check if all error properties are undefined (invalid/cross-origin error event)
  const hasNoErrorInfo =
    !event.message &&
    !event.filename &&
    !event.lineno &&
    !event.colno &&
    !event.error;

  if (hasNoErrorInfo) {
    return true;
  }

  // Filter out "Script error" which is usually from third-party scripts
  if (event.message === "Script error." || event.message === "") {
    return true;
  }

  // Check if error is related to contentDocument (common in third-party ad scripts)
  if (event.message && event.message.includes("contentDocument")) {
    return true;
  }

  // Check if error is from third-party domains (anymind, google, etc.)
  const thirdPartyDomains = [
    "anymind360.com",
    "googletagmanager.com",
    "google-analytics.com",
    "doubleclick.net",
  ];

  if (event.filename) {
    const isThirdParty = thirdPartyDomains.some((domain) =>
      event.filename?.includes(domain)
    );
    if (isThirdParty) {
      return true;
    }
  }

  return false;
};

// Override window.onerror to intercept errors before React's error overlay
const originalOnError = window.onerror;
window.onerror = function (
  message: string | Event,
  source?: string,
  lineno?: number,
  colno?: number,
  error?: Error
): boolean {
  // Create a synthetic error event to check
  const messageStr = typeof message === "string" ? message : "";
  const syntheticEvent = {
    message: messageStr,
    filename: source || "",
    lineno: lineno || 0,
    colno: colno || 0,
    error: error || null,
  } as ErrorEvent;

  if (shouldSuppressError(syntheticEvent)) {
    // Suppress third-party errors
    if (messageStr === "Script error." || messageStr === "") {
      console.warn("Third-party script error suppressed:", source);
    } else {
      console.warn("Third-party script error suppressed");
    }
    // Return true to indicate error was handled
    return true;
  }

  // Call original handler for legitimate errors
  if (originalOnError) {
    const result = originalOnError.call(
      this,
      message,
      source,
      lineno,
      colno,
      error ?? undefined
    );
    return result === true;
  }

  return false;
};

// Add global error handler to capture detailed error information
// Prevent third-party script errors from breaking the app
window.addEventListener(
  "error",
  (event) => {
    // Suppress third-party errors
    if (shouldSuppressError(event)) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    }

    // Only log detailed error information for legitimate application errors
    console.error("Application error:", {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error,
      stack: event.error?.stack,
    });

    // Allow other errors to propagate normally (these are likely real application errors)
    return true;
  },
  true
); // Use capture phase to catch errors earlier

// Handle unhandled promise rejections
window.addEventListener("unhandledrejection", (event) => {
  console.error("Unhandled promise rejection:", event.reason);
  // Prevent promise rejections from third-party scripts from breaking the app
  if (
    event.reason &&
    typeof event.reason === "string" &&
    event.reason.includes("Script error")
  ) {
    console.warn(
      "Third-party script promise rejection detected. Suppressing to prevent app breakage."
    );
    event.preventDefault();
  }
});

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
